import type { FC, ReactNode } from 'react'
import type { ServiceOption } from '@/types'

import { useRequestForm, useTodayDate } from '@/components/forms/use-request-form'
import { FormSuccess } from '@/components/forms/form-success'

import {
    Field,
    FormAlert,
    Honeypot,
    SelectInput,
    SubmitButton,
    TextArea,
    TextInput
} from '@/components/forms/fields'

interface BookingFormProps {
    services: Array<ServiceOption>
    initialService?: string
}

const GUEST_OPTIONS = [
    'Under 30 guests',
    '30 to 75 guests',
    '75 to 150 guests',
    '150 to 300 guests',
    '300 to 500 guests',
    'More than 500 guests'
]

const legendStyles = 'mb-5 flex items-center gap-3 font-heading text-2xl font-semibold text-brand-charcoal'
const stepStyles = 'inline-flex size-8 items-center justify-center rounded-full bg-brand-charcoal font-sans text-xs font-semibold text-brand-white'

export const BookingForm: FC<BookingFormProps> = ({ services, initialService = '' }): ReactNode => {
    const formId = 'booking'
    const today = useTodayDate()

    const {
        errors,
        status,
        reference,
        message,
        handleSubmit,
        reset,
        fieldProps
    } = useRequestForm({ type: 'booking', formId, initialValues: { service: initialService } })

    if (status === 'success') {
        return (
            <FormSuccess
                title='Thank you, your request is in.'
                description={(
                    <p>
                        We have received your catering request. Our team will contact you shortly on WhatsApp or by email to talk through your menu, confirm availability for your date and send your quote.
                    </p>
                )}
                reference={reference}
                whatsappMessage="Hello Loveth Tasty, I just sent a catering booking request on your website."
                onReset={reset}
                resetLabel='Book another event'
            />
        )
    }

    const serviceOptions = [...services.map(service => service.title), 'Not sure yet']

    return (
        <form id={formId} onSubmit={handleSubmit} noValidate className='relative space-y-10'>
            <Honeypot />

            <fieldset>
                <legend className={legendStyles}>
                    <span className={stepStyles} aria-hidden='true'>1</span>
                    Your details
                </legend>

                <div className='grid gap-5 sm:grid-cols-2'>
                    <Field id={`${formId}-name`} label='Full name' required error={errors.name}>
                        <TextInput {...fieldProps('name')} autoComplete='name' aria-required='true' placeholder='Your full name' />
                    </Field>

                    <Field id={`${formId}-phone`} label='Phone or WhatsApp' required error={errors.phone}>
                        <TextInput {...fieldProps('phone')} type='tel' autoComplete='tel' inputMode='tel' aria-required='true' placeholder='0803 000 0000' />
                    </Field>

                    <Field id={`${formId}-email`} label='Email address' required error={errors.email} className='sm:col-span-2'>
                        <TextInput {...fieldProps('email')} type='email' autoComplete='email' inputMode='email' aria-required='true' placeholder='you@example.com' />
                    </Field>
                </div>
            </fieldset>

            <fieldset>
                <legend className={legendStyles}>
                    <span className={stepStyles} aria-hidden='true'>2</span>
                    Your event
                </legend>

                <div className='grid gap-5 sm:grid-cols-2'>
                    <Field id={`${formId}-service`} label='Service you need' required error={errors.service}>
                        <SelectInput {...fieldProps('service')} aria-required='true' placeholder='Choose a service' options={serviceOptions} />
                    </Field>

                    <Field id={`${formId}-requested_date`} label='Event date' required error={errors.requested_date}>
                        <TextInput {...fieldProps('requested_date')} type='date' min={today || undefined} aria-required='true' />
                    </Field>

                    <Field id={`${formId}-guests`} label='Number of guests' required error={errors.guests}>
                        <SelectInput {...fieldProps('guests')} aria-required='true' placeholder='Choose a guest range' options={GUEST_OPTIONS} />
                    </Field>

                    <Field id={`${formId}-budget`} label='Estimated budget' error={errors.budget} hint='A rough figure helps us suggest the right menu.'>
                        <TextInput {...fieldProps('budget')} placeholder='For example, 750,000 naira' />
                    </Field>

                    <Field id={`${formId}-location`} label='Event location' required error={errors.location} className='sm:col-span-2'>
                        <TextInput {...fieldProps('location')} autoComplete='street-address' aria-required='true' placeholder='Venue name, area and city' />
                    </Field>

                    <Field id={`${formId}-message`} label='Tell us more' error={errors.message} className='sm:col-span-2'>
                        <TextArea {...fieldProps('message')} placeholder='Menu ideas, dishes you love, dietary needs, service style or timing' />
                    </Field>
                </div>
            </fieldset>

            <FormAlert message={message} />

            <div className='flex flex-col-reverse gap-4 border-t border-brand-sand pt-6 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-sm text-xs leading-relaxed text-brand-stone'>
                    We only use your details to respond to this request. Your quote is confirmed with you before anything is booked.
                </p>

                <SubmitButton submitting={status === 'submitting'} label='Request My Quote' />
            </div>
        </form>
    )
}
